// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from '../utils/axios'; 
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const login = async (method, credentials) => {
    setLoading(true);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
  
      let response;
      if (method === 'google') {
        response = await axios.post('/auth/login/google', {
          token: credentials.token,
          userInfo: null
        }, { signal: controller.signal });
      } else if (method === 'microsoft') {
        response = await axios.post('/auth/login/microsoft', {
          token: credentials.token,
          userInfo: null
        }, { signal: controller.signal });
      } else if (method === 'microsoft_code') {
        response = await axios.post('/auth/login/microsoft/code', {
          code: credentials.code,
          redirect_uri: credentials.redirect_uri
        }, { signal: controller.signal });
      } else {
        response = await axios.post('/auth/login', credentials, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          signal: controller.signal
        });
      }
  
      clearTimeout(timeoutId);
  
      // Check if MFA is required
      if (response.data.mfa_required) {
        return { 
          success: false, 
          error: {
            mfa_required: true,
            message: response.data.message,
            user_id: response.data.user_id
          }
        };
      }

      const { access_token, user } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(user);
      setIsAuthenticated(true);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      return { success: true, user };
    } catch (error) {
      if (error.name === 'AbortError') {
        return { success: false, error: 'Request timed out. Please try again.' };
      }
      return { success: false, error: error.response?.data?.detail || 'Invalid credentials' };
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessfulAuth = (authData) => {
    const { access_token, user } = authData;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(user);
    setIsAuthenticated(true);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
  };
  
  const linkGoogleAccount = async (token) => {
    try {
      const response = await axios.post('/api/link/google', { token });
      setUser(response.data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to link Google account');
    }
  };

  const unlinkGoogleAccount = async () => {
    try {
      const response = await axios.post('/api/unlink/google');
      setUser(response.data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to unlink Google account');
    }
  };

  const linkMicrosoftAccount = async (token) => {
    try {
      const response = await axios.post('/auth/link/microsoft', { token });
      setUser(response.data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to link Microsoft account');
    }
  };

  const linkMicrosoftAccountWithCode = async (code, redirectUri) => {
    try {
      const response = await axios.post('/auth/link/microsoft/code', { 
        code, 
        redirect_uri: redirectUri 
      });
      setUser(response.data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to link Microsoft account');
    }
  };

  const unlinkMicrosoftAccount = async () => {
    try {
      const response = await axios.post('/auth/unlink/microsoft');
      setUser(response.data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to unlink Microsoft account');
    }
  };

  const logout = useCallback(async () => {
    try {
      // Call backend logout endpoint to log the logout activity and terminate session
      if (token) {
        await axios.post('/auth/logout', {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      // Don't prevent logout if backend call fails
      console.warn('Backend logout failed:', error.response?.data?.detail || error.message);
    } finally {
      // Always clean up frontend state
      localStorage.removeItem('token');
      localStorage.removeItem('rememberedUser');
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  const refreshUser = useCallback(async () => {
    if (!token || !isAuthenticated) return;
    
    try {
      const response = await axios.post('/auth/verify-token', null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data.user);
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  }, [token, isAuthenticated]);

  // Verify token and restore session
  const checkAuth = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
  
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
  
      const response = await axios.post('/auth/verify-token', null, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal
      });
  
      clearTimeout(timeoutId);
      
      setUser(response.data.user);
      setIsAuthenticated(true);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } catch (error) {
      if (error.name === 'AbortError') {
        // Request timed out
      } else {
        console.error('Auth verification failed:', error);
      }
      logout();
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <AuthContext.Provider 
      value={{ 
        isAuthenticated, 
        user, 
        loading,
        token,
        login,
        linkGoogleAccount,
        unlinkGoogleAccount,
        linkMicrosoftAccount,
        linkMicrosoftAccountWithCode,
        unlinkMicrosoftAccount,
        logout,
        checkAuth,
        refreshUser,
        handleSuccessfulAuth
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;